import type { PersonType, WorkflowType } from '@td/core';
import { patchState, signalStore, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, Subject, switchMap, tap } from 'rxjs';
import type { CommonLogin, FederalLogin, LtsIncidentsList, LtsLogin, ParkingLogin, TowingLogin } from '../../types';
import { tapResponse } from '@ngrx/operators';
import {
  setError,
  setLoaded,
  setLoading,
  setResetState,
  withCallState,
  withReset,
  withStorageSync,
} from '@ngrx-toolkit/core';
import { type AuthResponse, AuthService } from './auth.service';

type State = {
  incidentId: string | null;
  personType: PersonType | null;
  personId: number | null;
  accessToken: string | null;
  workflowType: WorkflowType | null;
  ticketsList: LtsIncidentsList | null;
};

const initialState: State = {
  incidentId: null,
  personType: null,
  personId: null,
  accessToken: null,
  workflowType: null,
  ticketsList: null,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCallState(),
  withReset(),
  withStorageSync('td:auth'),
  withProps(() => {
    const _logoutSubject = new Subject<void>();
    const logout$ = _logoutSubject.asObservable();
    return { _logoutSubject, logout$, _api: inject(AuthService) };
  }),
  withMethods(({ _api, ...store }) => {
    function afterLogin(response: AuthResponse): void {
      const { accessToken, personId, personType, workflowType, workupId } = response;
      const incidentId = workupId.toString();
      patchState(store, { accessToken, personId, personType, workflowType, incidentId }, setLoaded());
      _api.navigateToHome(workflowType);
      _api.sendLoginEvent(incidentId);
    }

    function afterLoginError(error: unknown): void {
      patchState(store, setError(error));
      store.resetState();
    }

    const _loadLtsTicketsList = rxMethod<void>(
      pipe(
        tap(() => patchState(store, setLoading())),
        switchMap(() =>
          _api.ltsTickets().pipe(
            tapResponse({
              next: (res) => patchState(store, { ticketsList: res }, setLoaded()),
              error: (error) => afterLoginError(error),
            }),
          ),
        ),
      ),
    );

    const commonLogin = rxMethod<CommonLogin>(
      pipe(
        tap(() => patchState(store, setLoading())),
        switchMap((data) =>
          _api.commonLogin(data).pipe(
            tapResponse({
              next: (res) => afterLogin(res),
              error: (error) => afterLoginError(error),
            }),
          ),
        ),
      ),
    );

    const ctwLogin = rxMethod<CommonLogin>(
      pipe(
        tap(() => patchState(store, setLoading())),
        switchMap((data) =>
          _api.ctwLogin(data).pipe(
            tapResponse({
              next: (res) => afterLogin(res),
              error: (error) => afterLoginError(error),
            }),
          ),
        ),
      ),
    );

    const aftLogin = rxMethod<CommonLogin>(
      pipe(
        tap(() => patchState(store, setLoading())),
        switchMap((data) =>
          _api.aftLogin(data).pipe(
            tapResponse({
              next: (res) => afterLogin(res),
              error: (error) => afterLoginError(error),
            }),
          ),
        ),
      ),
    );

    const parkingLogin = rxMethod<ParkingLogin>(
      pipe(
        tap(() => patchState(store, setLoading())),
        switchMap((data) =>
          _api.parkingLogin(data).pipe(
            tapResponse({
              next: (res) => afterLogin(res),
              error: (error) => afterLoginError(error),
            }),
          ),
        ),
      ),
    );

    const towingLogin = rxMethod<TowingLogin>(
      pipe(
        tap(() => patchState(store, setLoading())),
        switchMap((data) =>
          _api.towingLogin(data).pipe(
            tapResponse({
              next: (res) => afterLogin(res),
              error: (error) => afterLoginError(error),
            }),
          ),
        ),
      ),
    );

    const federalLogin = rxMethod<FederalLogin>(
      pipe(
        tap(() => patchState(store, setLoading())),
        switchMap((data) =>
          _api.federalLogin(data).pipe(
            tapResponse({
              next: (res) => afterLogin(res),
              error: (error) => afterLoginError(error),
            }),
          ),
        ),
      ),
    );

    const loadLtsTickets = rxMethod<LtsLogin>(
      pipe(
        tap(() => patchState(store, setLoading())),
        switchMap((data) =>
          _api.ltsToken(data).pipe(
            tapResponse({
              next: ({ accessToken }) => {
                patchState(store, { accessToken }, setLoaded());
                _loadLtsTicketsList();
              },
              error: (error) => afterLoginError(error),
            }),
          ),
        ),
      ),
    );

    function ltsLogin(incidentId: string): void {
      const workflowType = 'LTS';
      patchState(store, { incidentId, workflowType });
      _api.navigateToHome(workflowType);
    }

    function saveLoginUrl(url: string): void {
      _api.saveLoginUrl(url);
    }

    function logout(url?: string): void {
      store._logoutSubject.next();
      store.resetState();
      store.clearStorage();
      _api.logout(url);
    }

    return {
      commonLogin,
      ctwLogin,
      parkingLogin,
      towingLogin,
      aftLogin,
      federalLogin,
      loadLtsTickets,
      ltsLogin,
      saveLoginUrl,
      logout,
    };
  }),
  withHooks({
    onInit(store) {
      setResetState(store, initialState);
    },
  }),
);
